import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import DataTable, { type TableColumn } from "react-data-table-component";
import { TablePager } from "../../components/shared/TablePager";
import { adminTableTheme } from "../../components/admin/adminTableTheme";
import { RelativeTimestamp } from "../../components/RelativeTimestamp";
import { useToast } from "../../components/toast/toastContext";
import { ErrorState, LoadingState } from "../../components/states";
import { formatPersonName } from "../../components/shared/personDisplay";
import { useSession } from "../../session/SessionContext";
import styles from "../../components/admin/Admin.module.css";
import panel from "../../components/admin/AdminPanel.module.css";
import pageStyles from "./AccountMatchingSections.module.css";
import {
  confirmIdentityCandidate,
  fetchSeatlessAllocations,
  fetchIdentityCandidates,
  markIdentityCandidateUnattributable,
  rejectIdentityCandidate,
  revokeSeatlessAllocation,
  type IdentityMatchCandidate,
  type SeatlessAllocation,
} from "../../api/identityQueue";
import { Tooltip } from "../../components/shared/Tooltip";

const AdministerPermission = "accounts.administer";
const RevokePermission = "entitlement.administer";

/**
 * Account matching (phase 21, `AIA-81`, building on phase 11's API), at the foot of System's Accounts tab: the provider accounts the
 * sync could not match to a person by itself, with their evidence (near-miss people and any unattributed consumption already attached to
 * the vendor identity) and confirm/reject/mark-unattributable actions, and the people allocated a provider who hold no seat with it. Both
 * are usually empty: the sync matches an account by its email, and creates the person when the email matches nobody. Shown only to a
 * holder of `accounts.administer`; the tab leaves it out for anyone else. Shows names and vendor identities as necessary evidence
 * for a match decision — never a "view this person's spend" feature; the unattributed-consumption lines shown are the vendor identity's
 * *own* unmatched consumption, not another person's attributed figures (`QR-06`/`OQ-12` stays untouched).
 */
export function AccountMatchingSections(): React.JSX.Element {
  return (
    <>
      <CandidatesSection />
      <SeatlessSection />
    </>
  );
}

/** The provider accounts waiting for an administrator to say whose they are. */
function CandidatesSection(): React.JSX.Element {
  const { t } = useTranslation();
  const [state, setState] = useState<{ kind: "loading" } | { kind: "loaded"; candidates: IdentityMatchCandidate[] } | { kind: "error"; message: string }>({
    kind: "loading",
  });

  const load = useCallback(async () => {
    setState({ kind: "loading" });
    const result = await fetchIdentityCandidates();
    if (result.kind === "ok") {
      setState({ kind: "loaded", candidates: result.response });
    } else if (result.kind === "denied") {
      setState({ kind: "error", message: t("admin.identity.denied.description") });
    } else {
      setState({ kind: "error", message: result.message });
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <section className={panel.panel} aria-labelledby="identity-candidates-heading">
      <div className={panel.header}>
        <h2 id="identity-candidates-heading" className={`panel-title ${panel.title}`}>
          {t("admin.identity.candidates.title")}
          {state.kind === "loaded" && <span className={pageStyles.count}>{state.candidates.length.toLocaleString()}</span>}
        </h2>
      </div>
      <p className={panel.intro}>{t("admin.identity.candidates.description")}</p>
      {state.kind === "loading" && <LoadingState label={t("admin.identity.loading")} />}
      {state.kind === "error" && <ErrorState description={state.message} onRetry={() => void load()} />}
      {state.kind === "loaded" &&
        (state.candidates.length === 0 ? (
          <p className={pageStyles.clear}>{t("admin.identity.candidates.empty")}</p>
        ) : (
          state.candidates.map((candidate) => <CandidateCard key={candidate.candidateId} candidate={candidate} onChanged={() => void load()} />)
        ))}
    </section>
  );
}

function CandidateCard({ candidate, onChanged }: { candidate: IdentityMatchCandidate; onChanged: () => void }): React.JSX.Element {
  const { t } = useTranslation();
  const toast = useToast();
  const [chosenPersonId, setChosenPersonId] = useState("");

  const handleConfirm = async () => {
    const result = await confirmIdentityCandidate(candidate.candidateId, chosenPersonId);
    if (result.kind === "ok") {
      onChanged();
    } else {
      toast.show(result.kind === "denied" ? t("admin.denied.description", { permissions: AdministerPermission }) : result.message, "error");
    }
  };

  const handleReject = async () => {
    const result = await rejectIdentityCandidate(candidate.candidateId);
    if (result.kind === "ok") {
      onChanged();
    } else {
      toast.show(result.kind === "denied" ? t("admin.denied.description", { permissions: AdministerPermission }) : result.message, "error");
    }
  };

  const handleMarkUnattributable = async () => {
    const result = await markIdentityCandidateUnattributable(candidate.candidateId);
    if (result.kind === "ok") {
      onChanged();
    } else {
      toast.show(result.kind === "denied" ? t("admin.denied.description", { permissions: AdministerPermission }) : result.message, "error");
    }
  };

  return (
    <div className={pageStyles.candidate}>
      <p>
        <strong>{candidate.vendorId}</strong> — {candidate.externalId} {candidate.email && `(${candidate.email})`}
      </p>
      <p className={styles.note}>{t("admin.identity.candidates.reason", { reason: candidate.matchReason })}</p>

      {candidate.nearMissPeople.length > 0 && (
        <ul>
          {candidate.nearMissPeople.map((person) => (
            // A near-miss row is evidence for a MultipleMatches decision, so the email stays visible
            // alongside the display-name-then-email label — but only when there is a real display
            // name to disambiguate from, since formatPersonName already falls back to the email
            // itself once a bootstrap-created person has none yet.
            <li key={person.personId}>
              {person.displayName.trim() ? `${formatPersonName(person)} (${person.email}) — ${person.personId}` : `${formatPersonName(person)} — ${person.personId}`}
            </li>
          ))}
        </ul>
      )}

      {candidate.unattributedConsumption.length > 0 && (
        <div className={styles.tableWrapper} tabIndex={0}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th scope="col">{t("admin.identity.candidates.period")}</th>
                <th scope="col">{t("admin.identity.candidates.metric")}</th>
                <th scope="col">{t("admin.identity.candidates.value")}</th>
              </tr>
            </thead>
            <tbody>
              {candidate.unattributedConsumption.map((line) => (
                <tr key={`${line.periodStart}-${line.metric}`}>
                  <td>{line.periodStart}</td>
                  <td>{line.metric}</td>
                  <td>
                    {line.value} {line.unit} {line.currency && `(${line.currency})`}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className={styles.form}>
        <div className={styles.field}>
          <label htmlFor={`confirm-person-${candidate.candidateId}`}>{t("admin.identity.candidates.confirmWithPersonId")}</label>
          <input
            id={`confirm-person-${candidate.candidateId}`}
            value={chosenPersonId}
            onChange={(event) => setChosenPersonId(event.target.value)}
            placeholder={t("admin.roles.form.personIdPlaceholder")}
          />
        </div>
        <button type="button" className="button button-primary" onClick={() => void handleConfirm()} disabled={!chosenPersonId}>
          {t("admin.identity.candidates.confirm")}
        </button>
        <button type="button" className="button button-secondary" onClick={() => void handleReject()}>
          {t("admin.identity.candidates.reject")}
        </button>
        <button type="button" className="button button-secondary" onClick={() => void handleMarkUnattributable()}>
          {t("admin.identity.candidates.markUnattributable")}
        </button>
      </div>
    </div>
  );
}

interface SeatlessRow extends SeatlessAllocation {
  rowKey: string;
}

/**
 * People the portal allocates a provider to who hold no seat with it. The administrator can revoke
 * the person's own allocation (an explicit two-step action, per row); access that comes from an
 * allowance group is shown but left to the group's own administration.
 */
function SeatlessSection(): React.JSX.Element {
  const { t } = useTranslation();
  const toast = useToast();
  const { snapshot } = useSession();
  const canRevoke = snapshot.status === "signed-in" && snapshot.permissions.includes(RevokePermission);
  const [state, setState] = useState<{ kind: "loading" } | { kind: "loaded"; rows: SeatlessRow[] } | { kind: "error"; message: string }>({
    kind: "loading",
  });
  const [confirming, setConfirming] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    setState({ kind: "loading" });
    const result = await fetchSeatlessAllocations();
    if (result.kind === "ok") {
      setState({ kind: "loaded", rows: result.response.map((item) => ({ ...item, rowKey: `${item.personId}|${item.vendorId}` })) });
    } else if (result.kind === "denied") {
      setState({ kind: "error", message: t("admin.identity.denied.description") });
    } else {
      setState({ kind: "error", message: result.message });
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleRevoke = useCallback(
    async (row: SeatlessRow) => {
      setBusy(row.rowKey);
      const result = await revokeSeatlessAllocation(row.personId, row.vendorId);
      setBusy(null);
      setConfirming(null);
      if (result.kind === "ok") {
        await load();
      } else {
        toast.show(result.kind === "denied" ? t("admin.denied.description", { permissions: RevokePermission }) : result.message, "error");
      }
    },
    [load, t, toast],
  );

  const columns = useMemo<TableColumn<SeatlessRow>[]>(
    () => [
      { id: "vendor", name: t("admin.entitlement.seatCosts.vendor"), selector: (row) => row.vendorId, sortable: true },
      {
        id: "person",
        name: t("admin.identity.seatless.person"),
        selector: (row) => formatPersonName(row),
        sortable: true,
        wrap: true,
        grow: 2,
        cell: (row) => <Tooltip label={row.email}>{formatPersonName(row)}</Tooltip>,
      },
      {
        id: "access",
        name: t("admin.identity.seatless.access"),
        selector: (row) => accessLabel(row, t),
        sortable: true,
        wrap: true,
        grow: 2,
      },
      {
        id: "seat",
        name: t("admin.identity.seatless.seat"),
        selector: (row) => row.seatLastSeenGoneAt ?? "",
        sortable: true,
        cell: (row) => (row.seatLastSeenGoneAt ? <RelativeTimestamp isoTimestamp={row.seatLastSeenGoneAt} /> : t("admin.identity.seatless.neverSeated")),
      },
      {
        id: "actions",
        name: <span className="visually-hidden">{t("dataTable.actionsColumn")}</span>,
        button: true,
        ignoreRowClick: true,
        minWidth: "16rem",
        cell: (row) => {
          if (!canRevoke || row.personalAmountUsd === null) {
            return null;
          }
          if (confirming !== row.rowKey) {
            return (
              <button type="button" className="button button-secondary" onClick={() => setConfirming(row.rowKey)}>
                {t("admin.identity.seatless.revoke")}
              </button>
            );
          }
          return (
            <div className={styles.inlineControls}>
              <button type="button" className="button button-primary" disabled={busy === row.rowKey} onClick={() => void handleRevoke(row)}>
                {t("admin.identity.seatless.confirmRevoke")}
              </button>
              <button type="button" className="button button-secondary" disabled={busy === row.rowKey} onClick={() => setConfirming(null)}>
                {t("admin.identity.seatless.cancel")}
              </button>
            </div>
          );
        },
      },
    ],
    [t, canRevoke, confirming, busy, handleRevoke],
  );

  return (
    <section className={panel.panel} aria-labelledby="seatless-heading">
      <div className={panel.header}>
        <h2 id="seatless-heading" className={`panel-title ${panel.title}`}>
          {t("admin.identity.seatless.title")}
          {state.kind === "loaded" && <span className={pageStyles.count}>{state.rows.length.toLocaleString()}</span>}
        </h2>
      </div>
      <p className={panel.intro}>{t("admin.identity.seatless.description")}</p>
      {state.kind === "loading" && <LoadingState label={t("admin.identity.loading")} />}
      {state.kind === "error" && <ErrorState description={state.message} onRetry={() => void load()} />}
      {state.kind === "loaded" &&
        (state.rows.length === 0 ? (
          <p className={pageStyles.clear}>{t("admin.identity.seatless.empty")}</p>
        ) : (
          <div className={`${styles.tableWrapper} ${styles.peopleTableWrapper}`}>
            <DataTable
              columns={columns}
              data={state.rows}
              className={styles.peopleDataTable}
              keyField="rowKey"
              ariaLabel={t("admin.identity.seatless.title")}
              theme={adminTableTheme}
              pagination
              paginationComponent={TablePager}
              paginationPerPage={25}
              paginationRowsPerPageOptions={[10, 25, 50, 100]}
              striped
              highlightOnHover
              columnSeparator="subtle"
            />
          </div>
        ))}
    </section>
  );
}

/** Where a seatless person's access comes from: their own allocation, an allowance group, or both. */
function accessLabel(row: SeatlessAllocation, t: (key: string, options?: Record<string, unknown>) => string): string {
  const parts: string[] = [];
  if (row.personalAmountUsd !== null) {
    parts.push(t("admin.identity.seatless.personal", { amount: row.personalAmountUsd }));
  }
  if (row.groupNames.length > 0) {
    parts.push(t("admin.identity.seatless.group", { groups: row.groupNames.join(", ") }));
  }
  return parts.join(" + ");
}
